import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, paymentKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';

/**
 * The answer to "has this bank payment landed yet?".
 *
 * `still_settling` is the healthy, common case — an ACH debit takes business
 * days — so it is reported as information rather than as a failure.
 */
export interface SettlementRefresh {
  status: 'settled' | 'voided' | 'still_settling' | 'needs_attention' | 'unavailable';
  message: string;
}

/**
 * The settlement notification can be lost, which used to strand a payment
 * PENDING with nobody able to do anything about it. This asks Payabli's ledger
 * directly — reading SettlementStatus, which answers "did the money arrive"
 * separately from "was it approved" — and applies the answer.
 */
export function useRefreshSettlement(invoiceId?: string | null) {
  const queryClient = useQueryClient();

  return useApiMutation<SettlementRefresh, { paymentId: string }>({
    mutationFn: ({ paymentId }) =>
      apiPost<SettlementRefresh, Record<string, never>>(
        operationsClient,
        invoiceEndpoints.refreshPaymentSettlement(paymentId),
        {},
      ),
    onSuccess: (result) => {
      // Only a resolved payment changes anything, but refetching either way
      // keeps the banner honest if something else moved in the meantime.
      if (invoiceId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      }
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all() });
      if (result.status === 'settled' || result.status === 'voided') {
        toast.success(result.message);
      } else if (result.status === 'needs_attention') {
        toast.error(result.message);
      } else {
        toast.info(result.message);
      }
    },
  });
}
